import React, { useState, useEffect, useCallback } from "react";
import ReactFlow, {
  Controls,
  Background,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  Panel
} from "reactflow";
import "reactflow/dist/style.css";
import {
  Button,
  TextField,
  Paper,
  Typography,
  Box,
  IconButton,
  Select,
  MenuItem,
  Modal
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import Dagre from '@dagrejs/dagre';

const initialNodes = [];
const initialEdges = [];

const CreateWorkflow = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState(null);
  const [entityName, setEntityName] = useState("");
  const [title, setTitle] = useState(""); 
  const [startEntity, setStartEntity] = useState("");
  const [finalEntities, setFinalEntities] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { getViewport, setViewport } = useReactFlow();
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [edgeMenu, setEdgeMenu] = useState({ visible: false, x: 0, y: 0, edge: null });
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/roles/all`);
        setRoles(res.data);
      } catch (err) {
        console.error('Failed to fetch roles:', err);
      }
    };

    fetchRoles();
  }, []);

  const onConnect = useCallback(
    (connection) => {
      const newEdge = { ...connection, markerEnd: { type: "arrowclosed" } };
      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges]
  );

  const addNode = () => {
    const currentViewport = getViewport(); 
  
    const newNode = {
      id: `${nodes.length + 1}`,
      type: "default",
      position: { x: Math.random() * 400, y: Math.random() * 400 },
      data: { label: `Entity ${nodes.length + 1}` },
    };
  
    setNodes([...nodes, newNode]);
  
    setTimeout(() => {
      setViewport(currentViewport);
    }, 0);
  };  

  const handleNodeClick = (event, node) => {
    setSelectedNode(node);
    setEntityName(node.data.label);
  };

  const updateNode = () => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === selectedNode.id
          ? { ...node, data: { ...node.data, label: entityName } }
          : node
      )
    );
  };

  const removeNode = () => {
    if (selectedNode) {
      setNodes((prevNodes) => prevNodes.filter((node) => node.id !== selectedNode.id));

      setEdges((prevEdges) =>
        prevEdges.filter((edge) => edge.source !== selectedNode.id && edge.target !== selectedNode.id)
      );

      if (startEntity === selectedNode.id) {
        setStartEntity("");
      }

      setFinalEntities((prev) => prev.filter((id) => id !== selectedNode.id));

      setSelectedNode(null);
      enqueueSnackbar("entity removed successfully", { variant: "success" });
    }
  };

  const handleEdgeClick = (event, edge) => {
    event.stopPropagation();
    setSelectedEdge(edge);

    setEdgeMenu({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      edge,
    });
  };

  const deleteEdge = () => {
    if (selectedEdge) {
      setEdges((prevEdges) => prevEdges.filter((e) => e.id !== selectedEdge.id));
      setSelectedEdge(null);
    }
  };

  const closeEditBox = () => {
    setSelectedNode(null);
  };

  const markAsStartingentity = () => {
    setStartEntity(selectedNode.id);
    enqueueSnackbar(`${selectedNode.data.label} is set as the Starting entity`, { variant: "info" });
  };
  
  const toggleFinalentity = () => {
    setFinalEntities((prev) => {
      if (prev.includes(selectedNode.id)) {
        return prev.filter((entityId) => entityId !== selectedNode.id);
      }
      return [...prev, selectedNode.id];
    });
  };  

  const isFinalentity = finalEntities.includes(selectedNode?.id);

  const getLayoutedElements = (nodes, edges) => {
    const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
    g.setGraph({
      rankdir: "TB",
      nodesep: 100,
      ranksep: 100,
      marginx: 20,
      marginy: 20,
    });
  
    edges.forEach((edge) => g.setEdge(edge.source, edge.target));
    nodes.forEach((node) =>
      g.setNode(node.id, { ...node, width: 120, height: 60 })
    );
  
    Dagre.layout(g);
  
    return {
      nodes: nodes.map((node) => ({
        ...node,
        position: { x: g.node(node.id).x, y: g.node(node.id).y },
      })),
      edges,
    };
  };  

  const onLayout = useCallback(
    (direction) => {
      console.log(nodes);
      const layouted = getLayoutedElements(nodes, edges, { direction });
 
      setNodes([...layouted.nodes]);
      setEdges([...layouted.edges]);
    },
    [setNodes, setEdges, nodes, edges],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (!title.trim()) {
      enqueueSnackbar("Title is required", { variant: "error" });
      return;
    }
  
    if (!startEntity) {
      enqueueSnackbar("Please set a starting entity", { variant: "error" });
      return;
    }

    const formattedNodes = nodes.map((node) => {
      return {
        id: node.id,
        name: node.data.label,
        outgoingEdges: edges
          .filter((edge) => edge.source === node.id)
          .map((edge) => edge.target),
      };
    });

    const outgoingFromStart = edges.filter(edge => edge.source === startEntity);

    if (outgoingFromStart.length > 1) {
      enqueueSnackbar("Starting entity cannot have more than one outgoing edge", { variant: "error" });
      return;
    }

    const workflowData = {
      title,
      startEntity,
      finalEntities,
      nodes: formattedNodes,
    };

    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/workflows/create`, workflowData);
      enqueueSnackbar("Workflow saved successfully", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to save workflow", { variant: "error" });
    }
  };
  

  return (
    <Paper elevation={3} sx={{ p: 3, width: "90vw", margin: "auto", mt: 5 }}>
      <Typography variant="h5" align="center" gutterBottom>
        Create a Workflow
      </Typography>

      <Box sx={{ display: "flex", gap: 2, position: "relative" }}>
        <Box sx={{ flex: selectedNode ? 2 : 1, transition: "all 0.3s ease-in-out" }}>
          <form onSubmit={handleSubmit}>
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              margin="normal"
              required
            />

            <Button
              variant="contained"
              color="primary"
              onClick={() => setIsModalOpen(true)}
              fullWidth
              sx={{ mt: 2 }}
            >
              Open Workflow Builder
            </Button>

            <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
              <Box sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "80vw",
                bgcolor: "white",
                boxShadow: 24,
                p: 4,
                borderRadius: 2
              }}>
                <Typography variant="h6" sx={{ mt: 2 }}>
                  Workflow Builder
                </Typography>

                <IconButton
                  onClick={() => setIsModalOpen(false)}
                  sx={{ position: "absolute", top: 8, right: 8 }}
                >
                  <CloseIcon />
                </IconButton>

                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                  <Button variant="contained" color="primary" onClick={addNode}>
                    Add entity
                  </Button>
                </Box>

                <Box sx={{ height: "400px", border: "1px solid #ddd", borderRadius: "8px" }}>
                    <ReactFlow
                      nodes={nodes}
                      edges={edges}
                      onNodesChange={onNodesChange}
                      onEdgesChange={onEdgesChange}
                      onConnect={onConnect}
                      onNodeClick={handleNodeClick}
                      onEdgeClick={handleEdgeClick}
                      proOptions={{ hideAttribution: true }}
                      defaultViewport={{ x: 0, y: 0, zoom: 1 }}
                    >
                      <Panel position='top-right'>
                        <Button
                          variant="contained"
                          color="secondary"
                          onClick={()=>{onLayout('TB')}}
                        >
                          Arrange Vertically
                        </Button>
                      </Panel>
                      <Controls />
                      <Background />
                    </ReactFlow>
                </Box>

                {selectedNode && (
                <Box
                  sx={{
                  width: "260px",
                  height: "350px",
                  position: "absolute",
                  right: "35px",
                  top: "60%",
                  transform: "translateY(-50%)",
                  background: "#fff",
                  borderLeft: "1px solid #ddd",
                  boxShadow: "-2px 0px 10px rgba(0,0,0,0.1)",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  transition: "all 0.3s ease-in-out",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography variant="h6">Edit entity</Typography>
                  <IconButton size="small" onClick={closeEditBox}>
                      <CloseIcon />
                  </IconButton>
                  </Box>

                  <Select
                    value={entityName}
                    onChange={(e) => setEntityName(e.target.value)}
                    required
                  >
                    {roles.map((role) => (
                      <MenuItem key={role._id} value={role.name}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>

                  <Button variant="contained" color="secondary" onClick={updateNode} fullWidth sx={{ mt: 1 }}>
                    Update
                  </Button>

                  <Button
                    variant="contained"
                    color={startEntity === selectedNode.id ? "success" : "primary"}
                    onClick={markAsStartingentity}
                    fullWidth
                    sx={{ mt: 1 }}
                  >
                    {startEntity === selectedNode.id ? "Starting entity ✔" : "Set as Starting entity"}
                  </Button>

                  <Button
                    variant="contained"
                    color={isFinalentity ? "success" : "primary"}
                    onClick={toggleFinalentity}
                    fullWidth
                    sx={{ mt: 1 }}
                  >
                    {isFinalentity ? "Final entity ✔" : "Set as Final entity"}
                  </Button>

                  <Button
                    variant="contained"
                    color="error"
                    onClick={removeNode}
                    fullWidth
                    sx={{ mt: 1 }}
                  >
                    Remove entity
                  </Button>
                </Box>
                )}

                {edgeMenu.visible && (
                  <Box
                    sx={{
                      position: "absolute",
                      left: edgeMenu.x,
                      top: edgeMenu.y,
                      backgroundColor: "white",
                      border: "1px solid #ddd",
                      boxShadow: "2px 2px 10px rgba(0,0,0,0.2)",
                      borderRadius: "4px",
                      zIndex: 1000,
                      p: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                    onMouseLeave={() => setEdgeMenu({ visible: false, x: 0, y: 0, edge: null })}
                  >
                    <Button onClick={deleteEdge} size="small" sx={{ textTransform: "none" }}>
                      Remove Edge
                    </Button>
                  </Box>
                )}
              </Box>
            </Modal>

            <Button 
              variant="contained" 
              color="info" 
              onClick={() => setIsPreviewOpen(true)} 
              fullWidth 
              sx={{ mt: 2 }}
            >
              Preview Workflow
            </Button>

            <Modal open={isPreviewOpen} onClose={() => setIsPreviewOpen(false)}>
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "80vw",
                  bgcolor: "white",
                  boxShadow: 24,
                  p: 4,
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Workflow Preview
                </Typography>

                <IconButton
                  onClick={() => setIsPreviewOpen(false)}
                  sx={{ position: "absolute", top: 8, right: 8 }}
                >
                  <CloseIcon />
                </IconButton>

                <Box sx={{ height: "400px", border: "1px solid #ddd", borderRadius: "8px" }}>
                  <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    fitView
                    proOptions={{ hideAttribution: true }}
                    defaultViewport={{ x: 0, y: 0, zoom: 0.75 }}
                  >
                    <Controls />
                    <Background />
                  </ReactFlow>
                </Box>
              </Box>
            </Modal>

            <Button variant="contained" color="success" type="submit" fullWidth sx={{ mt: 3 }}>
              Save Workflow
            </Button>
          </form>
        </Box>
      </Box>
    </Paper>
  );
};

export default CreateWorkflow;
